import { Slider } from '@client/components/ui/slider';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { Calendar, Check, Wallet } from 'lucide-react';
import { useFundingAllocationCard } from './hooks/useFundingAllocationCard';

interface FundingAllocationCardProps {
  assetType: LtcFinancialPlanningAssetType;
  accountDetail: LtcFinancialPlanningAccountDetail;
  allocation: number;
  isPriority: boolean;
  isSelected: boolean;
  yearsToLtc?: number;
  contributionMonthCount?: number;
  paymentOption?: LtcFinancialPlanningPaymentOption;
  fundingMode?: LtcFinancialPlanningFundingMode;
  onToggleSelection: () => void;
  onAllocationChange: (amount: number) => void;
  onFundingModeChange?: (mode: LtcFinancialPlanningFundingMode) => void;
  className?: string;
}

export function FundingAllocationCard({
  assetType,
  accountDetail,
  allocation,
  isSelected,
  yearsToLtc = 20,
  contributionMonthCount = 240,
  paymentOption = 'Single Pay',
  fundingMode = 'lumpSum',
  onToggleSelection,
  onAllocationChange,
  onFundingModeChange,
  className,
}: FundingAllocationCardProps) {
  const {
    assetLabel,
    paymentPeriodLabel,
    contributionTypeLabel,
    sliderMax,
    sliderStep,
    showModeToggle,
    projectedValue,
    roi,
    yearsToLtcRounded,
    remainingInAccount,
    remainingMonthlyContribution,
  } = useFundingAllocationCard({
    assetType,
    accountDetail,
    allocation,
    yearsToLtc,
    contributionMonthCount,
    paymentOption,
    fundingMode,
    onAllocationChange,
    onFundingModeChange,
  });

  return (
    <div
      className={cn(
        'rounded-xl border bg-white transition-all',
        {
          'border-primary/30': isSelected,
          'border-gray-200': !isSelected,
        },
        className,
      )}
    >
      {/* Header Section */}
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onToggleSelection}
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
              {
                'border-primary bg-primary': isSelected,
                'border-gray-300 bg-white': !isSelected,
              },
            )}
          >
            {isSelected && (
              <Check
                className="h-4 w-4 text-white"
                strokeWidth={3}
              />
            )}
          </button>

          <div>
            <span className="font-semibold text-gray-900">{assetLabel}</span>
            <div className="flex flex-wrap gap-x-1.5 text-sm text-gray-500">
              <span>
                Balance:{' '}
                <span className="text-gray-700">{formatCurrency(accountDetail.currentValue)}</span>
                <span>,</span>
              </span>
              {accountDetail.futureContributions > 0 && (
                <span>
                  Monthly Contribution:{' '}
                  <span className="text-gray-700">
                    {formatCurrency(accountDetail.futureContributions)}/mo
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {isSelected && (
          <div className="text-right">
            <p className="text-xs text-gray-500">Remaining at LTC</p>
            <p className="font-semibold text-green-500">{formatCurrency(projectedValue)}</p>
            <p className="text-xs text-gray-700">+{roi}% ROI</p>
          </div>
        )}
      </div>

      {/* Expanded Content when Selected */}
      {isSelected && (
        <div className="border-t border-gray-100 px-5 pb-5 pt-4">
          {/* Mode Toggle - Only show for non-Single Pay quotes with monthly contributions */}
          {showModeToggle && onFundingModeChange && (
            <ModeToggle
              fundingMode={fundingMode}
              currentValue={accountDetail.currentValue}
              monthlyContribution={accountDetail.futureContributions}
              onModeChange={onFundingModeChange}
            />
          )}

          {/* Slider Section */}
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">{contributionTypeLabel}</span>
            <span className="text-primary font-semibold">
              {formatCurrency(allocation)}
              {fundingMode === 'monthly' && '/mo'}
            </span>
          </div>

          <Slider
            value={[allocation]}
            onValueChange={values => onAllocationChange(values[0])}
            min={0}
            max={sliderMax}
            step={sliderStep}
            className="w-full"
          />

          {/* Summary Section */}
          <AllocationSummary
            fundingMode={fundingMode}
            allocation={allocation}
            remainingInAccount={remainingInAccount}
            remainingMonthlyContribution={remainingMonthlyContribution}
            paymentPeriodLabel={paymentPeriodLabel}
            projectedValue={projectedValue}
            yearsToLtcRounded={yearsToLtcRounded}
          />
        </div>
      )}
    </div>
  );
}

// Sub-components for better organization

interface ModeToggleProps {
  fundingMode: LtcFinancialPlanningFundingMode;
  currentValue: number;
  monthlyContribution: number;
  onModeChange: (mode: LtcFinancialPlanningFundingMode) => void;
}

function ModeToggle({
  fundingMode,
  currentValue,
  monthlyContribution,
  onModeChange,
}: ModeToggleProps) {
  return (
    <div className="mb-4">
      <p className="mb-2 text-sm font-medium text-gray-700">How would you like to contribute?</p>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onModeChange('lumpSum')}
          className={cn(
            'flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 transition-all',
            {
              'border-primary bg-primary/5 text-primary': fundingMode === 'lumpSum',
              'border-gray-200 bg-white text-gray-600 hover:border-gray-300':
                fundingMode !== 'lumpSum',
            },
          )}
        >
          <Wallet className="h-5 w-5" />
          <span className="text-sm font-medium">Lump Sum</span>
          <span className="text-xs opacity-75">{formatCurrency(currentValue)}</span>
        </button>
        <button
          type="button"
          onClick={() => onModeChange('monthly')}
          className={cn(
            'flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 transition-all',
            {
              'border-primary bg-primary/5 text-primary': fundingMode === 'monthly',
              'border-gray-200 bg-white text-gray-600 hover:border-gray-300':
                fundingMode !== 'monthly',
            },
          )}
        >
          <Calendar className="h-5 w-5" />
          <span className="text-sm font-medium">Monthly</span>
          <span className="text-xs opacity-75">{formatCurrency(monthlyContribution)}/mo</span>
        </button>
      </div>
    </div>
  );
}

interface AllocationSummaryProps {
  fundingMode: LtcFinancialPlanningFundingMode;
  allocation: number;
  remainingInAccount: number;
  remainingMonthlyContribution: number;
  paymentPeriodLabel: string;
  projectedValue: number;
  yearsToLtcRounded: number;
}

function AllocationSummary({
  fundingMode,
  allocation,
  remainingInAccount,
  remainingMonthlyContribution,
  paymentPeriodLabel,
  projectedValue,
  yearsToLtcRounded,
}: AllocationSummaryProps) {
  return (
    <div className="mt-6 space-y-3 rounded-lg bg-gray-50 p-3 text-sm">
      {fundingMode === 'lumpSum' ? (
        <>
          <div className="flex justify-between">
            <span className="text-gray-600">Total amount allocated</span>
            <span className="font-medium text-gray-900">{formatCurrency(allocation)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Remaining in account</span>
            <span className="font-medium text-gray-900">{formatCurrency(remainingInAccount)}</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-between">
            <span className="text-gray-600">Monthly amount</span>
            <span className="font-medium text-gray-900">{formatCurrency(allocation)}/mo</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Remaining monthly contribution</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(remainingMonthlyContribution)}/mo
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Payment period</span>
            <span className="font-medium text-gray-900">{paymentPeriodLabel}</span>
          </div>
        </>
      )}

      {/* Projected value at LTC */}
      <div className="mt-2 border-t border-gray-200 pt-2">
        <div className="flex justify-between">
          <span className="text-gray-600">Account value at LTC ({yearsToLtcRounded}yr)</span>
          <span className="font-semibold text-green-500">{formatCurrency(projectedValue)}</span>
        </div>
      </div>
    </div>
  );
}
